import React, { useCallback, useRef } from "react";

export interface CircularBpmKnobProps {
  value: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
  size?: number;
  className?: string;
}

/** Control circular compacto para tempo (arrastre vertical o en el arco). */
export const CircularBpmKnob: React.FC<CircularBpmKnobProps> = ({
  value,
  min = 40,
  max = 200,
  onChange,
  size = 44,
  className = "",
}) => {
  const dragging = useRef(false);
  const startY = useRef(0);
  const startVal = useRef(value);

  const pct = (value - min) / (max - min);
  const circumference = 2 * Math.PI * 14;
  const dash = pct * circumference * 0.75;
  const gap = circumference - dash;

  const clamp = (v: number) => Math.round(Math.min(max, Math.max(min, v)));

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      dragging.current = true;
      startY.current = e.clientY;
      startVal.current = value;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [value]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current) return;
      const dy = startY.current - e.clientY;
      const delta = Math.round(dy * 0.6);
      onChange(clamp(startVal.current + delta));
    },
    [min, max, onChange]
  );

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    dragging.current = false;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  }, []);

  return (
    <div
      className={`relative touch-none select-none cursor-ns-resize ${className}`}
      style={{ width: size, height: size }}
      role="slider"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-label="Tempo BPM"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <svg
        viewBox="0 0 36 36"
        className="w-full h-full -rotate-[135deg]"
        aria-hidden
      >
        <circle
          cx="18"
          cy="18"
          r="14"
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="3"
          strokeDasharray={`${circumference * 0.75} ${circumference * 0.25}`}
          strokeLinecap="round"
        />
        <circle
          cx="18"
          cy="18"
          r="14"
          fill="none"
          stroke="var(--bordona-accent, #ef4444)"
          strokeWidth="3"
          strokeDasharray={`${dash} ${gap + circumference * 0.25}`}
          strokeLinecap="round"
        />
      </svg>
      <span className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className="text-[11px] font-sans font-bold text-slate-100 tabular-nums leading-none">
          {value}
        </span>
        <span className="text-[7px] font-sans uppercase tracking-wider text-slate-500 mt-0.5">
          bpm
        </span>
      </span>
    </div>
  );
};
